import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { LanguageProvider, getPreferredLanguage } from './contexts/LanguageContext'
import { initialAuthRedirect } from './lib/supabase'
import AuthPage from './pages/AuthPage'
import Home from './pages/Home'
import OnboardingPage from './pages/OnboardingPage'
import IframePage from './pages/IframePage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import RefineStep from './components/steps/RefineStep'
import ProtectedRoute from './components/auth/ProtectedRoute'

// Import i18n to initialize it
import './i18n'

const RESET_PASSWORD_PATH = 'reset-password'

// Wrapper component that provides language context for language-prefixed routes
const LanguageRoutes = () => {
  return (
    <LanguageProvider>
      <Routes>
        <Route path="auth" element={<AuthPage />} />
        <Route path="forgot-password" element={<AuthPage initialMode="forgotPassword" />} />
        <Route path={RESET_PASSWORD_PATH} element={<ResetPasswordPage />} />
        <Route path="" element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        } />
        <Route path="onboarding" element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } />
        <Route path="refine" element={
          <ProtectedRoute>
            <RefineStep />
          </ProtectedRoute>
        } />
        <Route path="iframe" element={
          <ProtectedRoute>
            <IframePage />
          </ProtectedRoute>
        } />
      </Routes>
    </LanguageProvider>
  )
}

/**
 * Supabase appends recovery tokens to the URL as a hash fragment, and the link
 * may land on any route (the Site URL is used when no redirectTo is set).
 * Route those visits to the reset password page before any other redirect can
 * drop the hash, and keep the hash so the Supabase client can consume it.
 */
const PasswordRecoveryRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const { isPasswordRecovery } = useAuth()
  const [handled, setHandled] = useState(false)

  // Only the language-prefixed page is the final destination; a hash landing on
  // the legacy path still needs redirecting so the fragment survives.
  const onResetPasswordPage = new RegExp(`^/[a-z]{2}/${RESET_PASSWORD_PATH}/?$`).test(location.pathname)

  useEffect(() => {
    if (onResetPasswordPage) {
      setHandled(true)
    }
  }, [onResetPasswordPage])

  const isRecoveryLink =
    initialAuthRedirect.isRecovery || !!initialAuthRedirect.errorCode || isPasswordRecovery

  if (isRecoveryLink && !handled && !onResetPasswordPage) {
    return (
      <Navigate
        to={{
          pathname: `/${getPreferredLanguage()}/${RESET_PASSWORD_PATH}`,
          hash: initialAuthRedirect.hash,
        }}
        replace
      />
    )
  }

  return <>{children}</>
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <PasswordRecoveryRedirect>
          <Routes>
            {/* Redirect root to preferred language */}
            <Route path="/" element={<Navigate to={`/${getPreferredLanguage()}/`} replace />} />

            {/* Legacy routes without language prefix - redirect to language-prefixed version */}
            <Route path="/auth" element={<Navigate to={`/${getPreferredLanguage()}/auth`} replace />} />
            <Route path="/onboarding" element={<Navigate to={`/${getPreferredLanguage()}/onboarding`} replace />} />
            <Route path="/refine" element={<Navigate to={`/${getPreferredLanguage()}/refine`} replace />} />
            <Route path="/iframe" element={<Navigate to={`/${getPreferredLanguage()}/iframe`} replace />} />
            <Route path="/forgot-password" element={<Navigate to={`/${getPreferredLanguage()}/forgot-password`} replace />} />
            <Route path={`/${RESET_PASSWORD_PATH}`} element={<Navigate to={`/${getPreferredLanguage()}/${RESET_PASSWORD_PATH}`} replace />} />

            {/* Language-prefixed routes */}
            <Route path="/:lang/*" element={<LanguageRoutes />} />
          </Routes>
        </PasswordRecoveryRedirect>
      </Router>
    </AuthProvider>
  )
}

export default App
